import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";

import {
  TABLE_SPECIFIC_CONDITIONS_LIST,
  SPECIFIC_CONDITIONS_LIST_ENDPOINT,
  FILTER_TYPE_BUTTON,
  FILTER_TYPE_MULTI_SELECT_DROPDOWN,
} from "@utils/constants";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import Action from "./components/Action/Action";
import Country from "./components/Country/Country";
import GeneralInfo from "./components/GeneralInfo/GeneralInfo";
import { columnHeader, isEmpty } from "@utils/utils";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import CustomColumnFilter from "@shared/baseUI/CustomColumnFilter/CustomColumnFilter";

const PrepareDataTable = (props) => {
  const {
    t,
    isRefreshList,
    updateRefeshStatus,
    columnList,
    createNewSpecificCondition,
    countries,
  } = props;

  const [status, setStatus] = useState(2);
  const [data, setData] = useState([]);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const dataURL = SPECIFIC_CONDITIONS_LIST_ENDPOINT;
  const refreshData = (data) => {
    setRefreshState(data);
  };
  let columns = [];

  columns = [
    {
      Header: t("ID"),
      title: "ID",
      accessor: "id",
      disableFilters: true,
      Cell: ({ cell: { value } }) => <GeneralInfo data={value} column="id" />,
    },
    {
      Header: t("COUNTRY"),
      title: "COUNTRY",
      accessor: "country",
      Cell: ({ cell: { value } }) => <Country data={value} />,
      Filter: (filterProps) => (
        <CustomColumnFilter
          type={FILTER_TYPE_MULTI_SELECT_DROPDOWN}
          label={t("COUNTRY")}
          placeholder={t("SEARCH")}
          name="country"
          labelClass="products-list__country-label"
          containerClass="products-list__country-dropdown-container"
          inputClass=""
          defaultOption={[]}
          options={countries}
          // eslint-disable-next-line react/destructuring-assignment
          column={filterProps.column}
        />
      ),
    },
    {
      Header: t("LABEL"),
      title: "LABEL",
      accessor: "label",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="label" />
      ),
    },
    {
      Header: t("SPECIFIC_CONDITIONS_TITLE"),
      title: "SPECIFIC_CONDITIONS_TITLE",
      accessor: "title",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="title" />
      ),
    },
    {
      Header: t("SPECIFIC_CONDITIONS_PARAMETERS"),
      title: "SPECIFIC_CONDITIONS_PARAMETERS",
      accessor: "parameters",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="parameters" />
      ),
    },
    {
      Header: t("ACTIONS"),
      title: "ACTIONS",
      accessor: "action",
      Cell: ({ cell: { value, column } }) => (
        <Action
          data={value}
          column={column}
          onRefreshData={refreshData}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: true,
    },
  ];

  columns = columnHeader(columns, columnList);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({ globalFilter, sort, appliedFilters }) => {
    setRequestData({
      globalFilter,
      sort,
      appliedFilters,
    });
    // save request data,
    let country = [];
    if (appliedFilters !== undefined) {
      const countryFilter = appliedFilters.find(
        (filterItem) => filterItem.column === "country"
      );
      country =
        countryFilter?.value.length > 0
          ? countryFilter?.value
          : [1, 2, 3, 4, 5, 6, 7, 8];
    }
    // Set the loading state
    setLoading(true);
    axios
      .get(
        `/general/specificConditions?countries=[${country}]&status=${status}&orderBy=${
          sort ? sort.dir : "desc"
        }&orderColumn=${sort ? sort.column : "id"}&limit=-1`
      )
      .then((response) => {
        if (response?.data?.specificCondition.length > 0) {
          const specificConditionsList = response?.data?.specificCondition;
          const responseData = specificConditionsList.map(
            (specificCondition) => {
              const tempData = { ...specificCondition };
              tempData.action = { ...tempData };
              tempData.label = {
                label: tempData.label,
                status: tempData.isActive,
              };
              tempData.title = {
                title: tempData.title,
                status: tempData.isActive,
              };
              tempData.parameters = {
                parameters: tempData.parameters ? t("YES") : t("NO"),
                status: tempData.isActive,
              };
              tempData.id = {
                id: tempData.id,
                status: tempData.isActive,
              };

              tempData.country = {
                countryInfo: tempData.country,
                status: tempData.isActive,
              };
              return tempData;
            }
          );
          setData([...responseData]);
        } else setData([]);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        handleApiError(error);
      });
  };

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  const changeStatus = (label) => {
    let statusNew = status === 1 ? 2 : 1;
    setStatus(statusNew);
  };

  useEffect(() => {
    // everyTime we apply an custom filter we reset the page to 1
    if (!isEmpty(requestData)) {
      fetchRecords({
        globalFilter: requestData.globalFilter,
        sort: requestData.sort,
        appliedFilters: requestData.appliedFilters,
      });
    }
  }, [status]);

  return (
    <EncloseTable
      type={TABLE_SPECIFIC_CONDITIONS_LIST}
      columns={columns}
      data={data}
      setData={setData}
      initialPageSize={100}
      createNewAction={createNewSpecificCondition}
      isRefreshDataAgain={isRefreshDataAgain}
      setSkipPageReset={setSkipPageReset}
      skipPageReset={skipPageReset}
      pageCount={pageCount}
      fetchRecords={fetchRecords}
      loading={loading}
      defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
      extraFilters={[
        {
          type: FILTER_TYPE_BUTTON,
          label: "QUOTES_SPECIFIC_CONDITIONS_FILTER_ACTIVE_ONLY",
          name: "vat",
          labelClass: "sites-list__admins-list-label",
          containerClass: "sites-list__admins-list-dropdown-container",
          imageEnableIcon: enableIcon,
          imageDisableIcon: disableIcon,
          status,
          changeStatus: { changeStatus },
        },
      ]}
    />
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  columnList: PropTypes.array.isRequired,
};

export default withTranslation()(PrepareDataTable);
